// Section 24.14: the one <Overlay> host. Every overlay renders inside it: a dialog with focus
// trapping (Tab cycles inside), focus moved in on open and restored on close, and Esc to close
// unless the overlay owns its own Esc handling (the shortage dialog, Section 22).
import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  /** Id of the element naming the dialog. */
  labelledBy?: string;
  /** Accessible name when there is no visible title. */
  label?: string;
  /** Esc and the close button call this; omit when the overlay owns its own Esc. */
  onClose?: () => void;
  size?: 'normal' | 'wide' | 'full';
  children: ComponentChildren;
}

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export function OverlayHost({ labelledBy, label, onClose, size = 'normal', children }: Props) {
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = card.current;
    if (root !== null && !root.contains(document.activeElement)) {
      const preferred = root.querySelector<HTMLElement>('[data-autofocus]');
      // Keep the dialog scrolled to its top (a long report starts with the deaths).
      (preferred ?? focusables(root)[0] ?? root).focus({ preventScroll: true });
    }
    return () => {
      if (previous !== null && previous.isConnected) previous.focus();
    };
  }, []);
  return (
    <div class={`overlay overlay--${size}`}>
      <div
        ref={card}
        class="overlay__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={label}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && onClose !== undefined) {
            e.stopPropagation();
            onClose();
            return;
          }
          if (e.key !== 'Tab' || card.current === null) return;
          const items = focusables(card.current);
          const first = items[0];
          const last = items[items.length - 1];
          if (first === undefined || last === undefined) return;
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
